import { map, property } from "lodash";
import { SagaIterator } from "redux-saga";
import { all, put, select, take, takeEvery, throttle } from "redux-saga/effects";

import { ToastContent, getToastContent } from "constants/toasts";
import { pushWithUTM } from "helpers/sagas";
import { WelcomeTutorialRoutes } from "routes/welcome-tutorial/helpers";
import { RequestAction } from "store/entities/actions";
import { AGENT, AgentActions, selectAgentsAlphabetically } from "store/entities/agents";
import {
  INTEGRATION,
  IntegrationsActions,
  selectHasLiveChatIntegration,
  selectVisitedLiveChatIntegrationTutorial,
} from "store/entities/integrations";
import {
  ISendForwardingInstructionsFailurePayload,
  ISendForwardingInstructionsPayload,
  ISendForwardingInstructionsSuccessPayload,
  LICENSE,
  LicenseActions,
  selectDefaultMailboxUsed,
  selectDefaultTeamId,
} from "store/entities/license";
import { selectInitialReferrer } from "store/features/app";
import { getCurrentAgentId } from "store/features/session";
import { ToastVariant, ToastsActions } from "store/features/toasts";
import { IActionWithPayload } from "store/helper";
import { guessName } from "utils/agent";

import { WelcomeTutorialViewActions, WelcomeTutorialViewActionsNames } from "./actions";
import {
  IFinishWelcomeTutorialPayload,
  IInviteAgentsFailurePayload,
  IInviteAgentsPayload,
  IInviteAgentsSuccessPayload,
  IInvitedAgent,
  ISetCompanyNamePayload,
} from "./interfaces";
import { getPartialInvite } from "./selectors";

export const WelcomeTutorialRequestSource = "welcome-tutorial";

function* finish(action: IActionWithPayload<string, IFinishWelcomeTutorialPayload>): SagaIterator {
  const keepWelcomeTutorialOpened = action?.payload?.keepWelcomeTutorialOpened ?? false;
  const hasLiveChatIntegration = yield select(selectHasLiveChatIntegration);
  const visitedLiveChatIntegrationTutorial = yield select(selectVisitedLiveChatIntegrationTutorial);
  const initialReferrer = yield select(selectInitialReferrer);

  if (hasLiveChatIntegration && !visitedLiveChatIntegrationTutorial) {
    yield put(IntegrationsActions.markLiveChatIntegrationTutorialAsVisited());
    yield take([
      INTEGRATION.MARK_LIVECHAT_TUTORIAL_AS_VISITED[RequestAction.SUCCESS],
      INTEGRATION.MARK_LIVECHAT_TUTORIAL_AS_VISITED[RequestAction.FAILURE],
    ]);
  }

  const agentId = yield select(getCurrentAgentId);
  yield put(
    AgentActions.finishWelcomeTutorial({
      id: agentId,
      flags: {
        visitedWelcomeTutorial: true,
      },
    })
  );

  if (!keepWelcomeTutorialOpened) {
    yield put(pushWithUTM(initialReferrer));
  }
}

function* setCompanyName(action: IActionWithPayload<string, ISetCompanyNamePayload>): SagaIterator {
  const { companyName } = action.payload;

  yield put(LicenseActions.setCompanyName({ companyName, requestSource: WelcomeTutorialRequestSource }));

  if (companyName) {
    yield take(LICENSE.SET_COMPANY_NAME[RequestAction.SUCCESS]);
  }

  yield put(pushWithUTM(WelcomeTutorialRoutes.INVITE));
}

function* inviteAgents(action: IActionWithPayload<string, IInviteAgentsPayload>): SagaIterator {
  const payload = { ...action.payload };
  const agents = yield select(selectAgentsAlphabetically);
  const defaultTeamId = yield select(selectDefaultTeamId);
  const currentAgentsEmails = map(agents, property("email"));
  const filteredAgentsList = payload.agents.filter(
    (agent: IInvitedAgent) => !currentAgentsEmails.includes(agent.email)
  );

  const mappedPayload = {
    agents: filteredAgentsList.map((agent: IInvitedAgent) => ({
      name: guessName(agent.email),
      email: agent.email,
      teamIds: [defaultTeamId],
      permission: agent.permission,
    })),
    requestSource: WelcomeTutorialRequestSource,
  };

  yield put(AgentActions.batchCreateAgents(mappedPayload));
}

function* inviteAgentsSuccess(action: IActionWithPayload<string, IInviteAgentsSuccessPayload>): SagaIterator {
  const payload = { ...action.payload };
  if (payload.requestSource === WelcomeTutorialRequestSource) {
    yield put(
      ToastsActions.createToast({
        content: getToastContent(ToastContent.WELCOME_TUTORIAL_AGENTS_INVITE_SUCCESS, payload),
        variant: ToastVariant.Success,
      })
    );
    const partialInvite = yield select(getPartialInvite);
    const defaultInboxUsed = yield select(selectDefaultMailboxUsed);

    if (partialInvite.exceededBy > 0) {
      return;
    }

    if (defaultInboxUsed) {
      yield put(WelcomeTutorialViewActions.finish());
    }

    yield put(pushWithUTM(WelcomeTutorialRoutes.FORWARDING));
  }
}

function* inviteAgentsFailure(action: IActionWithPayload<string, IInviteAgentsFailurePayload>): SagaIterator {
  const payload = { ...action.payload };
  if (payload.requestSource === WelcomeTutorialRequestSource) {
    yield put(
      ToastsActions.createToast({
        content: getToastContent(ToastContent.WELCOME_TUTORIAL_AGENTS_INVITE_FAILURE, payload),
        variant: ToastVariant.Error,
      })
    );
  }
}

function* mailboxCopied(): SagaIterator {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.COPY_MAILBOX_SUCCESS),
      variant: ToastVariant.Success,
    })
  );
}

function* sendForwardingInstructions(
  action: IActionWithPayload<string, ISendForwardingInstructionsPayload>
): SagaIterator {
  const { recipientEmail } = action.payload;
  yield put(LicenseActions.sendForwardingInstructions({ recipientEmail, requestSource: WelcomeTutorialRequestSource }));
}

function* sendForwardingInstructionsSuccess(
  action: IActionWithPayload<string, ISendForwardingInstructionsSuccessPayload>
): SagaIterator {
  const { requestSource } = action.payload;
  if (requestSource === WelcomeTutorialRequestSource) {
    yield put(
      ToastsActions.createToast({
        content: getToastContent(ToastContent.FORWARDING_INSTRUCTIONS_SEND_SUCCESS),
        variant: ToastVariant.Success,
      })
    );
  }
}

function* sendForwardingInstructionsFailure(
  action: IActionWithPayload<string, ISendForwardingInstructionsFailurePayload>
): SagaIterator {
  const payload = { ...action.payload };
  if (payload.requestSource === WelcomeTutorialRequestSource) {
    yield put(
      ToastsActions.createToast({
        content: getToastContent(ToastContent.FORWARDING_INSTRUCTIONS_SEND_FAILURE),
        variant: ToastVariant.Error,
      })
    );
  }
}

export function* welcomeTutorialViewSagas(): SagaIterator {
  yield all([
    takeEvery(WelcomeTutorialViewActionsNames.FINISH, finish),
    takeEvery(WelcomeTutorialViewActionsNames.SET_COMPANY_NAME, setCompanyName),
    takeEvery(WelcomeTutorialViewActionsNames.INVITE_AGENTS, inviteAgents),
    takeEvery(AGENT.CREATE_BATCH[RequestAction.SUCCESS], inviteAgentsSuccess),
    takeEvery(AGENT.CREATE_BATCH[RequestAction.FAILURE], inviteAgentsFailure),
    throttle(1000, WelcomeTutorialViewActionsNames.MAILBOX_COPIED, mailboxCopied),
    takeEvery(WelcomeTutorialViewActionsNames.SEND_FORWARDING_INSTRUCTIONS, sendForwardingInstructions),
    takeEvery(LICENSE.SEND_FORWARDING_INSTRUCTIONS[RequestAction.SUCCESS], sendForwardingInstructionsSuccess),
    takeEvery(LICENSE.SEND_FORWARDING_INSTRUCTIONS[RequestAction.FAILURE], sendForwardingInstructionsFailure),
  ]);
}
